/**
 * Error message utilities for handling API and network errors
 */

interface ErrorResponse {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
  code?: string;
}

// Constants for error messages
const STATUS_MESSAGES: Record<number, string> = {
  404: 'The login service is not available. Please check your configuration or try again later.',
  401: 'Invalid email or password. Please check your credentials and try again.',
  403: 'Invalid email or password. Please check your credentials and try again.',
};

const NETWORK_ERROR_MESSAGE =
  'Unable to connect to the server. Please check your network connection.';
const SERVER_ERROR_MESSAGE = 'The server encountered an error. Please try again later.';
const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred. Please try again.';

// Network error codes
const NETWORK_ERROR_CODES = ['ERR_NETWORK', 'ECONNABORTED'];

/**
 * Check if error is a network error
 */
const isNetworkError = (error: ErrorResponse): boolean => {
  if (!error?.response) return true;
  if (error?.code && NETWORK_ERROR_CODES.includes(error.code)) return true;
  return false;
};

/**
 * Get message for specific status code
 */
const getStatusMessage = (status: number | undefined): string | null => {
  if (!status) return null;
  if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
  if (status >= 500) return SERVER_ERROR_MESSAGE;
  return null;
};

/**
 * Get API message if it should be used
 */
const getApiMessage = (
  status: number | undefined,
  apiMessage: string | undefined,
): string | null => {
  // Use API message for client errors (except 404)
  if (apiMessage && status !== 404 && status && status < 500) {
    return apiMessage;
  }
  return null;
};

/**
 * Get user-friendly error title based on error type
 * @param error - The error response object
 * @param defaultTitle - Optional custom default title (defaults to 'Request Failed')
 */
export const getErrorTitle = (error: ErrorResponse, defaultTitle = 'Request Failed'): string => {
  // Handle missing response (network error)
  if (!error?.response) return 'Connection Error';

  const status = error.response.status;

  // Map status codes to titles
  if (status === 404) return 'Service Unavailable';
  if (status === 401 || status === 403) return 'Authentication Failed';
  if (status && status >= 500) return 'Server Error';

  return defaultTitle;
};

/**
 * Get detailed error message based on error type
 */
export const getErrorMessage = (error: ErrorResponse): string => {
  const status = error?.response?.status;
  const apiMessage = error?.response?.data?.message;

  // Check for API message first
  const apiMsg = getApiMessage(status, apiMessage);
  if (apiMsg) return apiMsg;

  // Check for network errors
  if (isNetworkError(error)) return NETWORK_ERROR_MESSAGE;

  // Check for status-specific messages
  const statusMsg = getStatusMessage(status);
  if (statusMsg) return statusMsg;

  // Default fallback
  return apiMessage || DEFAULT_ERROR_MESSAGE;
};
